import React, { useEffect, useState } from 'react'
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface ClassOption {
  class_id: string
  name:     string
  status:   string
}

interface Props {
  open:           boolean
  studentId:      string | null
  studentLabel:   string
  onClose:        () => void
  onEnrolled:     () => void
}

export default function EnrollInClassDialog({ open, studentId, studentLabel, onClose, onEnrolled }: Props) {
  const [classes,    setClasses]    = useState<ClassOption[]>([])
  const [classId,    setClassId]    = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setClassId(''); setError(null)
    supabase
      .from('class')
      .select('class_id, name, status')
      .in('status', ['upcoming', 'active'])
      .order('name')
      .then(({ data }) => setClasses(data ?? []))
  }, [open])

  async function handleEnroll() {
    if (!studentId || !classId) return
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('enroll-in-class', {
      body: { student_id: studentId, class_id: classId },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSubmitting(false); return }
    if (data?.error) { setError(data.error); setSubmitting(false); return }
    setSubmitting(false)
    onEnrolled()
    onClose()
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Enroll in Class</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {studentLabel}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl size="small" fullWidth required>
          <InputLabel>Class</InputLabel>
          <Select label="Class" value={classId} onChange={e => setClassId(e.target.value)}>
            {classes.length === 0 && <MenuItem value="" disabled>(no enrollable classes)</MenuItem>}
            {classes.map(c => (
              <MenuItem key={c.class_id} value={c.class_id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleEnroll} disabled={submitting || !classId}>
          {submitting ? <CircularProgress size={20} /> : 'Enroll'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
